import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    MenuItem,
    Pagination,
    Popover,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import axios from 'axios';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/root-reducer';
import { findTalentUrl, dashboardUrl, auditionInviteUserUrl, talentUrl } from '../../configurations/api/url';
import { showToastMessage } from '../../utils/AppUtils';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { MessageDialogue } from '../../components/MessageDialogue/MessageDialogue';
import { ReusableDrawer } from '../../components/ReusableDrawer/ReusableDrawer';
import { WorkProfile } from '../../components/ScreenContainer/EsMyProfileContainer/WorkProfile';
import type { IWorkProfile } from '../../types/global/helper';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';

const TALENT_PAGE_LIMIT = 12;

// ─── Types ────────────────────────────────────────────────────────────────────
interface ITalentSkill {
    skill?: string;
    expertise?: string;
    years_of_experience?: number;
}

export interface ITalentProfile {
    _id: string;
    /** Role/title e.g. "Actor", "Actor | beauty model | content creator" */
    title?: string;
    profile_type?: string;
    stage_name?: string;
    profile_picture?: string;
    photos?: string[];
    gender?: string;
    /** Present on some discover payloads; used for optional client-side age refinement. */
    age?: number;
    ethnicity?: string[];
    hair_color?: string;
    hair_type?: string;
    body_type?: string;
    language_spoken?: string[];
    skills?: Array<ITalentSkill | null>;
    other_skills?: Array<ITalentSkill | null>;
    special_skills?: string[];
    rating?: number;
    number_of_reviews?: number;
    about?: string;
    height?: number;
    weight?: number;
    location?: { address?: string; coordinates?: number[] };
    verified?: boolean;
    user?: {
        _id?: string;
        firstName?: string;
        lastName?: string;
        userName?: string;
        profile_picture?: string;
    };
    created_by?: {
        _id?: string;
        firstName?: string;
        lastName?: string;
        userName?: string;
    };
    /** Whether the current logged-in user has saved/favourited this talent */
    has_user_saved?: boolean;
}

// ─── Shared filter styles ─────────────────────────────────────────────────────
const filterInputSx = {
    '& .MuiOutlinedInput-root': {
        bgcolor: 'rgba(255,255,255,0.05)',
        borderRadius: 2,
        fontSize: 13,
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,201,101,0.4)' },
    },
    '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
};

const filterMenuProps = {
    PaperProps: {
        sx: {
            maxHeight: 280,
            bgcolor: '#1a1919',
            border: '1px solid rgba(255,255,255,0.07)',
            '& .MuiMenuItem-root': {
                fontSize: 13,
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&.Mui-selected': { bgcolor: 'rgba(255,201,101,0.1)' },
            },
        },
    },
};

const filterSelectSx = {
    width: '100%',
    bgcolor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
    fontSize: 13,
    color: '#fff',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,201,101,0.4)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
    '& .MuiSelect-select': { color: '#fff' },
};

// ─── Invite to audition modal ─────────────────────────────────────────────────
interface IGig {
    _id: string;
    title?: string;
    status?: string;
    category?: string;
    casting_expiration?: string;
    imageCover?: string;
    totalApplications?: number;
    tags?: string[];
}

function InviteToAuditionModal({
    talent,
    token,
    onClose,
}: {
    talent: ITalentProfile | null;
    token: string;
    onClose: () => void;
}) {
    const [gigs, setGigs] = useState<IGig[]>([]);
    const [loadingGigs, setLoadingGigs] = useState(false);
    const [statusMap, setStatusMap] = useState<Record<string, 'idle' | 'loading' | 'done' | 'error'>>({});

    const userId = talent?.user?._id || talent?.created_by?._id || '';

    const displayName =
        talent?.stage_name?.trim() ||
        [talent?.user?.firstName, talent?.user?.lastName].filter(Boolean).join(' ') ||
        'Unknown';

    useEffect(() => {
        if (!talent) return;
        setStatusMap({});
        setLoadingGigs(true);
        axios
            .get(`${dashboardUrl}?limit=40&page=1&category=gigs`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => {
                const payload = res.data;
                const list: IGig[] = payload?.data?.gigs?.data ?? [];
                setGigs(Array.isArray(list) ? list : []);
            })
            .catch(() => setGigs([]))
            .finally(() => setLoadingGigs(false));
    }, [talent, token]);

    const handleInvite = async (gig: IGig) => {
        const gigId = gig._id;
        if (!gigId || !userId) return;
        setStatusMap(prev => ({ ...prev, [gigId]: 'loading' }));
        try {
            await axios.post(
                auditionInviteUserUrl(gigId),
                { userId },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setStatusMap(prev => ({ ...prev, [gigId]: 'done' }));
            showToastMessage('Invite sent successfully', 'success');
        } catch {
            setStatusMap(prev => ({ ...prev, [gigId]: 'error' }));
            showToastMessage('Failed to send invite. Please try again.', 'error');
        }
    };

    return (
        <Dialog
            open={Boolean(talent)}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { bgcolor: '#1a1919', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)' } }}>
            <DialogTitle sx={{ p: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2.5,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 17, color: '#fff' }}>
                            Invite to job / audition
                        </Typography>
                        {talent && (
                            <Typography
                                sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 13, color: '#adaaaa', mt: 0.25 }}>
                                {displayName}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
                {loadingGigs ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_LUXE_PRIMARY }} />
                    </Box>
                ) : gigs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 14, color: '#adaaaa' }}>
                            No auditions or gigs found.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 420, overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {gigs.map((gig, i) => {
                            const gigId = gig._id;
                            const gigTitle = gig.title || `Gig ${i + 1}`;
                            const gigStatus = gig.status || 'open';
                            const status = statusMap[gigId] ?? 'idle';
                            return (
                                <Box key={gigId}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            px: 3,
                                            py: 2,
                                        }}>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff' }}
                                                noWrap>
                                                {gigTitle}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 11,
                                                    color: '#adaaaa',
                                                    textTransform: 'capitalize',
                                                    mt: 0.25,
                                                }}>
                                                {gigStatus}
                                            </Typography>
                                        </Box>
                                        {status === 'done' ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CheckCircleOutlineIcon
                                                    sx={{ fontSize: 18, color: BRAND_LUXE_SECONDARY }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                                        fontSize: 13,
                                                        color: BRAND_LUXE_SECONDARY,
                                                    }}>
                                                    Invited
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                disabled={status === 'loading'}
                                                onClick={() => handleInvite(gig)}
                                                sx={{
                                                    flexShrink: 0,
                                                    borderRadius: 999,
                                                    textTransform: 'none',
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 13,
                                                    px: 2,
                                                    color: '#392700',
                                                    bgcolor: status === 'error' ? '#ff6b6b' : BRAND_LUXE_PRIMARY,
                                                    minWidth: 80,
                                                    '&:hover': { bgcolor: '#feb700' },
                                                    '&.Mui-disabled': {
                                                        bgcolor: 'rgba(255,201,101,0.3)',
                                                        color: 'rgba(0,0,0,0.4)',
                                                    },
                                                }}>
                                                {status === 'loading' ? (
                                                    <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                                ) : status === 'error' ? (
                                                    'Retry'
                                                ) : (
                                                    'Invite'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                    {i < gigs.length - 1 && (
                                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', mx: 3 }} />
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BrandTalentTab({
    activeTab,
    onTabChange,
}: {
    activeTab: 'influencer' | 'talent';
    onTabChange: (tab: 'influencer' | 'talent') => void;
}) {
    const token: string = useSelector((s: RootState) => s.authentication.token);

    // ─── Invite modal state ───────────────────────────────────────────────────
    const [inviteTarget, setInviteTarget] = useState<ITalentProfile | null>(null);
    const [profileTarget, setProfileTarget] = useState<ITalentProfile | null>(null);
    const [messageTarget, setMessageTarget] = useState<ITalentProfile | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [profiles, setProfiles] = useState<ITalentProfile[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    // Draft filter state
    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [draftGender, setDraftGender] = useState('');
    const [draftAgeFrom, setDraftAgeFrom] = useState('');
    const [draftAgeTo, setDraftAgeTo] = useState('');
    const [draftRating, setDraftRating] = useState('');
    const [draftProfileType, setDraftProfileType] = useState('');
    const [draftEthnicity, setDraftEthnicity] = useState('');
    const [draftBodyType, setDraftBodyType] = useState('');
    const [draftHairColor, setDraftHairColor] = useState('');
    const [draftLanguages, setDraftLanguages] = useState('');
    const [draftSkills, setDraftSkills] = useState('');

    // Applied filter state
    const [applied, setApplied] = useState({
        gender: '',
        ageFrom: '',
        ageTo: '',
        rating: '',
        profileType: '',
        ethnicity: '',
        bodyType: '',
        hairColor: '',
        languages: '',
        skills: '',
    });

    const activeFilterCount = Object.values(applied).filter(Boolean).length;
    const [favoritesOnly, setFavoritesOnly] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────────────
    const fetchProfiles = useCallback(
        async (opts: typeof applied & { search: string; page: number; favoritesOnly: boolean }) => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (opts.search) params.set('searchTerm', opts.search);
                if (opts.gender) params.set('gender', opts.gender);
                if (opts.ageFrom) params.set('ageFrom', opts.ageFrom);
                if (opts.ageTo) params.set('ageTo', opts.ageTo);
                if (opts.rating) params.set('rating', opts.rating);
                if (opts.profileType) params.set('profile_type', opts.profileType);
                if (opts.ethnicity) params.set('ethnicity', opts.ethnicity);
                if (opts.bodyType) params.set('body_type', opts.bodyType);
                if (opts.hairColor) params.set('hair_color', opts.hairColor);
                if (opts.languages) params.set('language_spoken', opts.languages);
                if (opts.skills) params.set('skills', opts.skills);
                params.set('page', String(opts.page));
                params.set('limit', String(TALENT_PAGE_LIMIT));
                if (opts.favoritesOnly) params.set('favorites_only', 'true');

                const res = await axios.get(`${findTalentUrl}?${params.toString()}`, {
                    signal: controller.signal,
                    ...(token && { headers: { Authorization: `Bearer ${token}` } }),
                });

                if (!controller.signal.aborted) {
                    const payload = res.data;
                    const list: ITalentProfile[] = payload?.data ?? payload?.profiles ?? payload?.results ?? [];
                    setProfiles(Array.isArray(list) ? list : []);
                    setTotalPages(payload?.totalPages ?? 1);
                    setTotalCount(payload?.totalCount ?? list.length);
                }
            } catch (err: any) {
                if (!axios.isCancel(err)) {
                    setProfiles([]);
                    setTotalPages(1);
                    setTotalCount(0);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        },
        [token],
    );

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 350);
        return () => clearTimeout(t);
    }, [searchInput]);

    // Fetch on change
    useEffect(() => {
        fetchProfiles({ search, page, ...applied, favoritesOnly });
    }, [search, page, applied, favoritesOnly, fetchProfiles]);

    // Cancel on unmount
    useEffect(() => () => abortRef.current?.abort(), []);

    // ─── Favourite toggle ─────────────────────────────────────────────────────
    const toggleFavorite = useCallback(
        async (profile: ITalentProfile) => {
            if (!token) return;
            const isFav = profile.has_user_saved;
            setProfiles(prev => prev.map(p => (p._id === profile._id ? { ...p, has_user_saved: !isFav } : p)));
            try {
                if (isFav) {
                    await axios.delete(`${talentUrl}/${profile._id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                } else {
                    await axios.post(
                        talentUrl,
                        { talent_id: profile._id },
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                }
            } catch {
                setProfiles(prev => prev.map(p => (p._id === profile._id ? { ...p, has_user_saved: isFav } : p)));
            }
        },
        [token],
    );

    // ─── Filter handlers ──────────────────────────────────────────────────────
    const handleApply = () => {
        setApplied({
            gender: draftGender,
            ageFrom: draftAgeFrom,
            ageTo: draftAgeTo,
            rating: draftRating,
            profileType: draftProfileType,
            ethnicity: draftEthnicity,
            bodyType: draftBodyType,
            hairColor: draftHairColor,
            languages: draftLanguages,
            skills: draftSkills,
        });
        setPage(1);
        setFilterAnchor(null);
    };

    const handleClear = () => {
        setDraftGender('');
        setDraftAgeFrom('');
        setDraftAgeTo('');
        setDraftRating('');
        setDraftProfileType('');
        setDraftEthnicity('');
        setDraftBodyType('');
        setDraftHairColor('');
        setDraftLanguages('');
        setDraftSkills('');
        setApplied({
            gender: '',
            ageFrom: '',
            ageTo: '',
            rating: '',
            profileType: '',
            ethnicity: '',
            bodyType: '',
            hairColor: '',
            languages: '',
            skills: '',
        });
        setPage(1);
        setFilterAnchor(null);
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <>
            <Box sx={{ mb: 4 }}>
                {/* Tab switcher — right above search */}
                <Box
                    sx={{
                        display: 'flex',
                        mb: 2,
                        p: 0.5,
                        bgcolor: BRAND_SURFACE_CONTAINER,
                        borderRadius: 999,
                        width: 'fit-content',
                        gap: 0.5,
                    }}>
                    {[
                        { key: 'influencer' as const, label: 'Influencers' },
                        { key: 'talent' as const, label: 'Talent' },
                    ].map(tab => (
                        <Button
                            key={tab.key}
                            onClick={() => onTabChange(tab.key)}
                            disableRipple={activeTab === tab.key}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                px: 3,
                                py: 0.75,
                                minWidth: 110,
                                bgcolor: activeTab === tab.key ? BRAND_LUXE_PRIMARY : 'transparent',
                                color: activeTab === tab.key ? '#392700' : '#adaaaa',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    bgcolor: activeTab === tab.key ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.06)',
                                    color: activeTab === tab.key ? '#392700' : '#fff',
                                },
                            }}>
                            {tab.label}
                        </Button>
                    ))}
                </Box>

                {/* Search + filter */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name, skill, or type..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#adaaaa', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#262626',
                                borderRadius: 999,
                                py: 0.75,
                                pl: 1,
                                pr: 2,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 15,
                                color: '#ffffff',
                                '& fieldset': { border: 'none' },
                                '&:hover fieldset': { border: 'none' },
                                '&.Mui-focused fieldset': { border: 'none' },
                                '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(255,201,101,0.35)' },
                            },
                            '& .MuiInputBase-input::placeholder': { color: 'rgba(173,170,170,0.5)', opacity: 1 },
                        }}
                    />
                    <IconButton
                        onClick={() => {
                            setFavoritesOnly(f => !f);
                            setPage(1);
                        }}
                        sx={{
                            flexShrink: 0,
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: favoritesOnly ? 'rgba(255,105,180,0.12)' : '#262626',
                            color: favoritesOnly ? '#ff69b4' : '#adaaaa',
                            border: favoritesOnly ? '1px solid rgba(255,105,180,0.35)' : '1px solid transparent',
                            '&:hover': { bgcolor: 'rgba(255,105,180,0.15)', color: '#ff69b4' },
                        }}>
                        {favoritesOnly ? (
                            <FavoriteIcon sx={{ fontSize: 20 }} />
                        ) : (
                            <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                        )}
                    </IconButton>
                    <Badge
                        badgeContent={activeFilterCount}
                        color="warning"
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 10,
                            },
                        }}>
                        <IconButton
                            onClick={e => setFilterAnchor(e.currentTarget)}
                            sx={{
                                flexShrink: 0,
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                bgcolor: activeFilterCount > 0 ? 'rgba(255,201,101,0.12)' : '#262626',
                                color: activeFilterCount > 0 ? BRAND_LUXE_PRIMARY : '#adaaaa',
                                border:
                                    activeFilterCount > 0 ? `1px solid rgba(255,201,101,0.3)` : '1px solid transparent',
                                '&:hover': { bgcolor: 'rgba(255,201,101,0.15)', color: BRAND_LUXE_PRIMARY },
                            }}>
                            <TuneIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Badge>
                </Box>

                {/* Filter popover */}
                <Popover
                    open={Boolean(filterAnchor)}
                    anchorEl={filterAnchor}
                    onClose={() => setFilterAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{
                        sx: {
                            mt: 1,
                            bgcolor: '#1a1919',
                            border: '1px solid rgba(255,255,255,0.07)',
                            borderRadius: 3,
                            p: 2.5,
                            width: 320,
                        },
                    }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: '#fff', mb: 2 }}>
                        Filters
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            maxHeight: 420,
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { display: 'none' },
                        }}>
                        <Select
                            size="small"
                            displayEmpty
                            value={draftProfileType}
                            onChange={e => setDraftProfileType(e.target.value)}
                            sx={filterSelectSx}
                            MenuProps={filterMenuProps}>
                            <MenuItem value="">Any profile type</MenuItem>
                            <MenuItem value="content_creator">Content Creator</MenuItem>
                            <MenuItem value="performer">Performer</MenuItem>
                            <MenuItem value="voiceover">Voiceover</MenuItem>
                            <MenuItem value="technical">Technical</MenuItem>
                        </Select>
                        <Select
                            size="small"
                            displayEmpty
                            value={draftGender}
                            onChange={e => setDraftGender(e.target.value)}
                            sx={filterSelectSx}
                            MenuProps={filterMenuProps}>
                            <MenuItem value="">Any gender</MenuItem>
                            <MenuItem value="male">Male</MenuItem>
                            <MenuItem value="female">Female</MenuItem>
                            <MenuItem value="non-binary">Non-binary</MenuItem>
                        </Select>

                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    color: 'rgba(255,255,255,0.35)',
                                    letterSpacing: '0.06em',
                                    mb: 0.75,
                                    textTransform: 'uppercase',
                                }}>
                                Age range
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    size="small"
                                    placeholder="From"
                                    value={draftAgeFrom}
                                    onChange={e => setDraftAgeFrom(e.target.value.replace(/\D/g, ''))}
                                    sx={filterInputSx}
                                    inputProps={{ maxLength: 3 }}
                                />
                                <TextField
                                    size="small"
                                    placeholder="To"
                                    value={draftAgeTo}
                                    onChange={e => setDraftAgeTo(e.target.value.replace(/\D/g, ''))}
                                    sx={filterInputSx}
                                    inputProps={{ maxLength: 3 }}
                                />
                            </Box>
                        </Box>

                        <Select
                            size="small"
                            displayEmpty
                            value={draftRating}
                            onChange={e => setDraftRating(e.target.value)}
                            sx={filterSelectSx}
                            MenuProps={filterMenuProps}>
                            <MenuItem value="">Any rating</MenuItem>
                            <MenuItem value="5">5 stars</MenuItem>
                            <MenuItem value="4">4+ stars</MenuItem>
                            <MenuItem value="3">3+ stars</MenuItem>
                            <MenuItem value="2">2+ stars</MenuItem>
                        </Select>

                        <TextField
                            size="small"
                            placeholder="Profile type (e.g. Actor, Model)"
                            value={draftProfileType}
                            onChange={e => setDraftProfileType(e.target.value)}
                            sx={filterInputSx}
                        />

                        <TextField
                            size="small"
                            placeholder="Ethnicity (comma-separated)"
                            value={draftEthnicity}
                            onChange={e => setDraftEthnicity(e.target.value)}
                            sx={filterInputSx}
                        />

                        <Select
                            size="small"
                            displayEmpty
                            value={draftBodyType}
                            onChange={e => setDraftBodyType(e.target.value)}
                            sx={filterSelectSx}
                            MenuProps={filterMenuProps}>
                            <MenuItem value="">Any body type</MenuItem>
                            <MenuItem value="slim">Slim</MenuItem>
                            <MenuItem value="athletic">Athletic</MenuItem>
                            <MenuItem value="average">Average</MenuItem>
                            <MenuItem value="curvy">Curvy</MenuItem>
                            <MenuItem value="plus-size">Plus size</MenuItem>
                            <MenuItem value="muscular">Muscular</MenuItem>
                            <MenuItem value="any">Any</MenuItem>
                        </Select>

                        <TextField
                            size="small"
                            placeholder="Hair color"
                            value={draftHairColor}
                            onChange={e => setDraftHairColor(e.target.value)}
                            sx={filterInputSx}
                        />

                        <TextField
                            size="small"
                            placeholder="Languages (comma-separated)"
                            value={draftLanguages}
                            onChange={e => setDraftLanguages(e.target.value)}
                            sx={filterInputSx}
                        />

                        <TextField
                            size="small"
                            placeholder="Skills (comma-separated)"
                            value={draftSkills}
                            onChange={e => setDraftSkills(e.target.value)}
                            sx={filterInputSx}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
                        <Button
                            fullWidth
                            size="small"
                            onClick={handleClear}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: '#adaaaa',
                                bgcolor: 'rgba(255,255,255,0.06)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                            }}>
                            Clear
                        </Button>
                        <Button
                            fullWidth
                            size="small"
                            onClick={handleApply}
                            sx={{
                                borderRadius: 999,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 13,
                                color: '#392700',
                                bgcolor: BRAND_LUXE_PRIMARY,
                                '&:hover': { bgcolor: '#feb700' },
                            }}>
                            Apply
                        </Button>
                    </Box>
                </Popover>
            </Box>

            {/* Result count */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 11,
                        bgcolor: loading ? 'rgba(255,255,255,0.05)' : 'rgba(0,253,193,0.1)',
                        color: loading ? '#adaaaa' : BRAND_LUXE_SECONDARY,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        letterSpacing: '0.06em',
                    }}>
                    {loading ? '…' : `${totalCount} FOUND`}
                </Typography>
            </Box>

            {/* Grid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
                    <CircularProgress size={40} sx={{ color: BRAND_LUXE_PRIMARY }} />
                </Box>
            ) : profiles.length === 0 ? (
                <TalentEmptyState search={search} />
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                        gap: { xs: 3, md: 4 },
                    }}>
                    {profiles.map(profile => (
                        <TalentCard
                            key={profile._id}
                            profile={profile}
                            onInvite={() => setInviteTarget(profile)}
                            onViewProfile={() => setProfileTarget(profile)}
                            onMessage={() => setMessageTarget(profile)}
                            onFavorite={() => toggleFavorite(profile)}
                        />
                    ))}
                </Box>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        size="medium"
                        sx={{
                            '& .MuiPaginationItem-root': { color: '#adaaaa', fontSize: 13 },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Invite to audition modal */}
            <InviteToAuditionModal talent={inviteTarget} token={token} onClose={() => setInviteTarget(null)} />

            {/* Talent work-profile drawer (not the influencer/creator slider) */}
            <ReusableDrawer
                open={Boolean(profileTarget)}
                onClose={() => setProfileTarget(null)}
                anchor="right"
                content={
                    profileTarget ? (
                        <WorkProfile
                            isEditMode={false}
                            workProfile={[profileTarget as unknown as IWorkProfile]}
                            isPreviewMode
                            external
                            searchFlow
                            onMessageApplication={() => {
                                setMessageTarget(profileTarget);
                                setProfileTarget(null);
                            }}
                            onInviteClick={() => {
                                setInviteTarget(profileTarget);
                                setProfileTarget(null);
                            }}
                        />
                    ) : null
                }
            />

            {/* DM dialogue */}
            {messageTarget && (
                <MessageDialogue
                    open={Boolean(messageTarget)}
                    onClose={() => setMessageTarget(null)}
                    recipient={
                        messageTarget.user?._id || messageTarget.created_by?._id
                            ? ({
                                  _id: messageTarget.user?._id ?? messageTarget.created_by?._id ?? '',
                                  firstName:
                                      messageTarget.user?.firstName ??
                                      messageTarget.created_by?.firstName ??
                                      messageTarget.stage_name ??
                                      '',
                                  lastName: messageTarget.user?.lastName ?? messageTarget.created_by?.lastName ?? '',
                                  profile_picture:
                                      messageTarget.user?.profile_picture ?? messageTarget.profile_picture ?? '',
                                  verified: false,
                                  role: '',
                                  audme_profile: '',
                              } as any)
                            : undefined
                    }
                />
            )}
        </>
    );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function TalentEmptyState({ search }: { search: string }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                py: 12,
                border: '2px dashed rgba(255,255,255,0.06)',
                borderRadius: 3,
            }}>
            <Box
                sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <PersonOutlineIcon sx={{ fontSize: 30, color: 'rgba(255,255,255,0.15)' }} />
            </Box>
            <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 15, color: 'rgba(255,255,255,0.4)' }}>
                No talent found
            </Typography>
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.25)',
                    textAlign: 'center',
                    maxWidth: 300,
                }}>
                {search
                    ? `No talent matched "${search}". Try a different keyword.`
                    : 'No talent profiles are available for this filter yet.'}
            </Typography>
        </Box>
    );
}

// ─── Talent card ──────────────────────────────────────────────────────────────
function TalentCard({
    profile,
    onInvite,
    onViewProfile,
    onMessage,
    onFavorite,
}: {
    profile: ITalentProfile;
    onInvite: () => void;
    onViewProfile: () => void;
    onMessage: () => void;
    onFavorite: () => void;
}) {
    // Resolve display name: stage_name > user name > created_by name
    const displayName =
        profile.stage_name?.trim() ||
        [profile.user?.firstName, profile.user?.lastName].filter(Boolean).join(' ') ||
        [profile.created_by?.firstName, profile.created_by?.lastName].filter(Boolean).join(' ') ||
        'Unknown';

    // Cover image: profile_picture > first photo
    const coverImage = profile.profile_picture ?? profile.photos?.[0];

    // Title to show in badge — truncate if long
    const titleLabel = profile.title
        ? profile.title.split('|')[0].trim()
        : profile.profile_type
        ? profile.profile_type
        : null;

    // Location
    const locationLabel = profile.location?.address ?? null;

    // Skills (from skills + other_skills). API may include null entries or skills missing `skill`.
    const allSkills = [...(profile.skills ?? []), ...(profile.other_skills ?? [])]
        .map(s => s?.skill)
        .filter((name): name is string => Boolean(name))
        .slice(0, 4);

    // Languages
    const langs = (profile.language_spoken ?? []).map(l => l.charAt(0).toUpperCase() + l.slice(1)).slice(0, 3);

    return (
        <Box
            onClick={onViewProfile}
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.05)',
                transition: 'box-shadow 0.4s',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                '&:hover': { boxShadow: '0 20px 50px rgba(0,0,0,0.5)' },
            }}>
            {/* Image area */}
            <Box
                sx={{
                    height: 220,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover img': { transform: 'scale(1.05)' },
                }}>
                {coverImage ? (
                    <Box
                        component="img"
                        src={coverImage}
                        alt={displayName}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }}
                    />
                ) : (
                    <Typography sx={{ fontSize: 56, color: 'rgba(255,255,255,0.12)', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        {displayName[0]?.toUpperCase() ?? '?'}
                    </Typography>
                )}

                {/* Title badge (top right) */}
                {titleLabel && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 14,
                            right: 14,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            color: '#fff',
                            textTransform: 'capitalize',
                        }}>
                        {titleLabel}
                    </Box>
                )}

                {/* Rating badge (top left) */}
                {profile.rating !== undefined && profile.rating > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 14,
                            left: 14,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.4,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 999,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            border: '1px solid rgba(255,201,101,0.25)',
                        }}>
                        <StarRoundedIcon sx={{ fontSize: 12, color: BRAND_LUXE_PRIMARY }} />
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                color: BRAND_LUXE_PRIMARY,
                                lineHeight: 1,
                            }}>
                            {profile.rating.toFixed(1)}
                        </Typography>
                    </Box>
                )}
                {/* Favourite + chat icons — bottom right of image */}
                <Box
                    sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 0.75 }}
                    onClick={e => e.stopPropagation()}>
                    <IconButton
                        onClick={onFavorite}
                        sx={{
                            width: 34,
                            height: 34,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            border: profile.has_user_saved
                                ? '1px solid rgba(255,105,180,0.5)'
                                : '1px solid rgba(255,255,255,0.15)',
                            color: profile.has_user_saved ? '#ff69b4' : '#fff',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.15)', bgcolor: 'rgba(255,105,180,0.15)' },
                        }}>
                        {profile.has_user_saved ? (
                            <FavoriteIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                    <IconButton
                        onClick={onMessage}
                        sx={{
                            width: 34,
                            height: 34,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            border: `1px solid ${BRAND_LUXE_PRIMARY}66`,
                            color: BRAND_LUXE_PRIMARY,
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.15)', bgcolor: 'rgba(255,201,101,0.15)' },
                        }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Info */}
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                {/* Name + location */}
                <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 19, mb: 0.25 }} noWrap>
                        {displayName}
                    </Typography>
                    {locationLabel && (
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: '#adaaaa' }} noWrap>
                            {locationLabel}
                        </Typography>
                    )}
                </Box>

                {/* Attribute pills */}
                {(profile.body_type || profile.hair_color || langs.length > 0) && (
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                        {profile.body_type && profile.body_type !== 'any' && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: '#adaaaa',
                                        textTransform: 'capitalize',
                                    }}>
                                    {profile.body_type}
                                </Typography>
                            </Box>
                        )}
                        {profile.hair_color && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: '#adaaaa',
                                        textTransform: 'capitalize',
                                    }}>
                                    {profile.hair_color} hair
                                </Typography>
                            </Box>
                        )}
                        {langs.length > 0 && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: 999,
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    border: '1px solid rgba(255,255,255,0.06)',
                                }}>
                                <Typography sx={{ fontSize: 10, fontFamily: FONT_ENUM.MAIN_BOLD, color: '#adaaaa' }}>
                                    {langs.join(' · ')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Skills */}
                {allSkills.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2.5 }}>
                        {allSkills.map(skill => (
                            <Chip
                                key={skill}
                                label={skill}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    bgcolor: 'rgba(255,201,101,0.08)',
                                    color: BRAND_LUXE_PRIMARY,
                                    border: '1px solid rgba(255,201,101,0.18)',
                                    '& .MuiChip-label': { px: 1.25 },
                                }}
                            />
                        ))}
                    </Box>
                )}

                <Box sx={{ flex: 1 }} />

                <Button
                    fullWidth
                    onClick={e => {
                        e.stopPropagation();
                        onInvite();
                    }}
                    sx={{
                        py: 1.75,
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: 13,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: '#392700',
                        background: `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        '&:hover': { boxShadow: '0 0 20px rgba(255,201,101,0.35)', filter: 'brightness(1.05)' },
                    }}>
                    Invite to job
                </Button>
            </Box>
        </Box>
    );
}
